import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  CircularProgress,
  IconButton,
  Typography,
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import CloseIcon from "@material-ui/icons/Close";
import WarningIcon from "@material-ui/icons/Warning";

import uuid from "react-uuid";

import store from "../../../store";
import TemplateTabs from "../TemplateTabs";
import { fieldModel } from "../TemplateField/common";
import { generateTemplatesToJSON } from "../../../utils/templateUtils";

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  saveButton: {
    width: 150,
  },
  circularProgress: {
    margin: "0 auto",
    display: "block",
  },
  agreeModal: {
    display: "flex",
    alignItems: "center",
  },
  agreeModalIcon: {
    marginRight: "5px",
  },
  addTemplateButton: {
    border: `2px solid ${theme.palette.primary.main}`,
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const TemplatePopup = withStyles(styles)(
  ({
    open = false,
    popupLoading = false,
    onClose,
    onSave,
    templates = [],
    setTemplates,
    classes,
    loader = false,
    setOpen,
  }) => {
    const [hasError, setError] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [isChange, setIsChange] = useState(false);
    const [templateNameError, setTemplateNameError] = useState(false);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);

    useEffect(() => {
      if (!open) {
        setIsChange(false);
      }
    }, [open]);

    useEffect(() => {
      setTemplateNameError(checkNames(templates));
    }, [templates]);

    const addTab = () => {
      setTemplates([
        ...templates,
        {
          name: {
            value: "New template",
            error: true,
          },
          description: {
            value: "",
            error: false,
          },
          fields: [fieldModel(uuid())],
        },
      ]);
      setError(true);
      setTemplateNameError(true);
      setCurrentTab(templates.length);
      setIsChange(true);
    };

    const addField = (templateIndex = 0) => {
      setTemplates([
        ...templates.map((data, i) => {
          const name = `field_${templates[templateIndex].fields.length}`;
          const nameError =
            templates[templateIndex].fields.filter(
              (data) => data.name.value === name
            ).length > 0;
          if (nameError) {
            setError(true);
          }
          return {
            ...data,
            ...(i === templateIndex && {
              fields: [
                ...templates[templateIndex].fields,
                {
                  id: uuid(),
                  name: {
                    value: name,
                    error: nameError,
                  },
                  type: {
                    value: "string",
                    error: false,
                  },
                  value: {
                    value: "value",
                    error: false,
                  },
                },
              ],
            }),
          };
        }),
      ]);
      setIsChange(true);
    };

    //Check template on error exist after deleting field.
    const recursiveSearchError = (templateFields) => {
      for (let i = 0; i < templateFields.length; i++) {
        const { name, value, type } = templateFields[i];
        if (name.error || value.error || type.error) {
          setError(true);
          return;
        }
        if (Array.isArray(templateFields[i]?.value.value)) {
          recursiveSearchError(templateFields[i].value.value);
        }
      }
      setError(false);
    };

    const deleteField = ({ templateIndex = 0, idToDelete }) => {
      (function recursiveSearchForDelete(templateFields) {
        for (let i = 0; i < templateFields.length; i++) {
          if (templateFields[i].id === idToDelete) {
            templateFields.splice(i, 1);
            recursiveSearchError(templates[templateIndex].fields);
            return;
          }
          if (Array.isArray(templateFields[i]?.value.value)) {
            recursiveSearchForDelete(templateFields[i].value.value);
          }
        }
      })(templates[templateIndex].fields);
      setTemplates([...templates]);
      setIsChange(true);
    };

    const changeField = ({ templateIndex = 0, nodeId, ...fields }) => {
      (function recursiveSearchForEdit(templateFields) {
        for (let i = 0; i < templateFields.length; i++) {
          if (templateFields[i].id === nodeId) {
            const { name, type, value } = fields;

            const checkNameError =
              !name.value ||
              templateFields.filter((item, index) => {
                return i !== index && name.value === item.name.value;
              }).length > 0;

            const checkValueError =
              type.value !== "boolean"
                ? !value.value
                : typeof value.value === "undefined";
            checkValueError || checkNameError
              ? setError(true)
              : setError(false);

            templateFields[i] = {
              id: nodeId,
              name: {
                value: name.value,
                error: checkNameError,
              },
              type,
              value: {
                value: value.value,
                error: checkValueError,
              },
            };

            return;
          }

          if (Array.isArray(templateFields[i]?.value.value)) {
            recursiveSearchForEdit(templateFields[i].value.value);
          }
        }
      })(templates[templateIndex].fields);

      setTemplates([...templates]);
      setIsChange(
        JSON.stringify(generateTemplatesToJSON(templates)) !==
          JSON.stringify(store.getState().templates)
      );
    };

    const changeName = ({ templateIndex = 0, name }) => {
      setTemplates([
        ...templates.map((data, i) => {
          const error =
            !name.value ||
            templates.filter(({ name: { value } }) => value === name.value)
              .length > 0;
          setTemplateNameError(error);
          return {
            ...data,
            ...(i === templateIndex && {
              name: {
                ...name,
                error,
              },
            }),
          };
        }),
      ]);
      setIsChange(true);
    };
    const changeDescription = ({ templateIndex = 0, description = "" }) => {
      setTemplates([
        ...templates.map((data, i) => {
          const error = !description.value;
          setTemplateNameError(error);
          return {
            ...data,
            ...(i === templateIndex && {
              description: description,
            }),
          };
        }),
      ]);
      setIsChange(true);
    };

    const checkNames = (arr) =>
      arr.filter(
        ({ name: { value } }, i) =>
          !value ||
          arr.some(
            (template, index) => template.name.value === value && i !== index
          )
      ).length > 0;

    // Delete template method
    const deleteTemplate = (templateIndex) => {
      const newArr = templates.filter((_, index) => index !== templateIndex);
      setTemplates(newArr);
      setCurrentTab(newArr.length ? newArr.length - 1 : 0);
      setIsChange(true);
    };

    const isDisabled = useMemo(
      () =>
        Boolean(
          hasError ||
            templateNameError ||
            !templates.length ||
            templates.filter(({ fields = [] }) => !fields.length).length
        ) || !isChange,
      [hasError, isChange, templates, templateNameError]
    );

    const handleClose = () => {
      if (isChange) {
        setOpenConfirmModal(true);
      } else {
        onClose();
      }
    };

    return (
      <>
        <Dialog
          aria-labelledby="customized-dialog-title-save-changes"
          open={openConfirmModal}
          maxWidth="sm"
        >
          <DialogContent>
            <div className={classes.agreeModal}>
              <WarningIcon className={classes.agreeModalIcon} />
              Changes not saved. Close without saving?
            </div>
          </DialogContent>
          <DialogActions>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenConfirmModal(false);
                }}
                color="primary"
              >
                Disagree
              </Button>
              <Button
                onClick={() => {
                  setOpenConfirmModal(false);
                  setOpen(false);
                  setError(false);
                  setTemplateNameError(false);
                }}
                color="primary"
                autoFocus
              >
                Agree
              </Button>
            </DialogActions>
          </DialogActions>
        </Dialog>
        <Dialog
          onClose={handleClose}
          aria-labelledby="customized-dialog-title"
          open={open}
          fullWidth={true}
          maxWidth="xl"
        >
          <DialogTitle id="customized-dialog-title" onClose={handleClose}>
            Templates
          </DialogTitle>
          <DialogContent dividers>
            <header>
              <Button
                className={classes.addTemplateButton}
                onClick={() => addTab()}
                color="primary"
              >
                Add Template
              </Button>
            </header>
            {loader ? (
              <CircularProgress className={classes.circularProgress} />
            ) : (
              <TemplateTabs
                templates={templates}
                setTemplates={setTemplates}
                deleteTemplate={deleteTemplate}
                changeName={changeName}
                changeDescription={changeDescription}
                addField={addField}
                changeField={changeField}
                deleteField={deleteField}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                setError={setError}
                setIsChange={setIsChange}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button
              autoFocus
              className={classes.saveButton}
              onClick={onSave}
              color="primary"
              disabled={isDisabled}
            >
              {popupLoading ? <CircularProgress size={20} /> : "Save changes"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

TemplatePopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  templates: PropTypes.array.isRequired,
  setTemplates: PropTypes.func.isRequired,
  loader: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default TemplatePopup;
